import React from 'react';
import { Typography, Tag, Space, Avatar, Empty, Progress } from 'antd';
import { TeamOutlined, UserOutlined } from '@ant-design/icons';
import { formatDisplayName } from '../utils/moduleUtils';
import { UngradedParticipant } from '../types/training';

const { Title, Text } = Typography;

interface ParticipantListProps {
    participants: UngradedParticipant[];
    emptyDescription: string;
}

const statusFor = (submitted: number, total: number) => {
    if (submitted === 0) return { label: 'Not started', color: '#8c8c8c' };
    if (submitted === total) return { label: 'Fully submitted', color: '#389e0d' };
    return { label: 'In progress', color: '#d46b08' };
};

const ParticipantList: React.FC<ParticipantListProps> = ({ participants, emptyDescription }) => {
    if (participants.length === 0) {
        return <Empty description={emptyDescription} style={{ padding: '60px 0' }} />;
    }

    return (
        <div style={{
            background: 'linear-gradient(135deg, #f9f0ff 0%, #f5eaff 100%)',
            border: '1px solid #d3adf7',
            borderRadius: 12,
            padding: '20px 24px'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 8 }}>
                <TeamOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                <Title level={4} style={{ margin: 0, color: '#531dab' }}>Participants</Title>
                <Tag color="purple" style={{ marginLeft: 4 }}>{participants.length} enrolled</Tag>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #d3adf7' }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#8c8c8c' }}>PARTICIPANT</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#8c8c8c', width: 140 }}>STATUS</th>
                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: '#8c8c8c', width: 180 }}>SUBMITTED</th>
                        </tr>
                    </thead>
                    <tbody>
                        {participants.map((p, idx) => {
                            const pct = p.total > 0 ? Math.round((p.submitted / p.total) * 100) : 0;
                            const status = statusFor(p.submitted, p.total);
                            return (
                                <tr key={p.email} style={{ borderBottom: '1px solid #f0e6fa', background: idx % 2 === 1 ? 'rgba(255,255,255,0.5)' : 'transparent' }}>
                                    <td style={{ padding: '10px 12px' }}>
                                        <Space>
                                            <Avatar icon={<UserOutlined />} style={{ backgroundColor: p.submitted > 0 ? '#9254de' : '#bfbfbf', flexShrink: 0 }} />
                                            <div>
                                                <div style={{ fontWeight: 500, fontSize: 14 }}>{formatDisplayName(p.fullname, p.email)}</div>
                                                <div style={{ fontSize: 11, color: '#8c8c8c' }}>{p.email}</div>
                                            </div>
                                        </Space>
                                    </td>
                                    <td style={{ padding: '10px 12px' }}>
                                        <Tag color={status.label === 'Fully submitted' ? 'green' : status.label === 'In progress' ? 'orange' : 'default'} style={{ borderRadius: 10 }}>
                                            {status.label}
                                        </Tag>
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                        <Text strong style={{ fontSize: 14, color: status.color }}>{p.submitted} / {p.total}</Text>
                                        <Progress
                                            percent={pct}
                                            size="small"
                                            showInfo={false}
                                            strokeColor={p.submitted === p.total ? '#52c41a' : p.submitted > 0 ? '#fa8c16' : '#d9d9d9'}
                                            style={{ marginBottom: 0, marginTop: 4 }}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ParticipantList;
